import pluralize from 'pluralize';

import { needsImgixServerSign, resolveByosImgixSrc, withByosImgixServerSign } from '@air/component-imgix-image';

import { type CanvasTokenNode } from '../store/slices/createCanvasTokenNodeSlice';

const VIDEO_EXTENSIONS = new Set(['.mp4', '.mov', '.webm', '.avi', '.m4v']);
const MAX_TEXT_LABEL_LENGTH = 20;

/** Check whether a URL points to a video file based on its extension. */
export const isVideoUrl = (url: string): boolean => {
  try {
    const pathname = new URL(url).pathname;
    const ext = pathname.substring(pathname.lastIndexOf('.')).toLowerCase();

    return VIDEO_EXTENSIONS.has(ext);
  } catch {
    return false;
  }
};

export type GetImageThumbnailUrlOptions = {
  width?: number;
  height?: number;
};

/**
 * Build a small thumbnail URL for imgix-hosted images/videos by appending size/fit params.
 * `fit=clip` letterboxes so logos and board photos stay fully visible in the square thumb.
 * For video URLs, adds `video-thumbnail=auto&fm=jpg` so imgix extracts a frame as an image.
 * Returns the original URL unchanged for non-imgix sources.
 */
export const getImageThumbnailUrl = (src: string, options: GetImageThumbnailUrlOptions): string => {
  try {
    const resolved = resolveByosImgixSrc(src);
    const url = new URL(resolved);

    if (!url.hostname.includes('imgix.net')) {
      return src;
    }

    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const params: Record<string, string | number> = {
      auto: 'format',
      dpr,
      fit: 'clip',
    };

    if (options.width) {
      params.w = options.width;
    }

    if (options.height) {
      params.h = options.height;
    }

    const ext = url.pathname.substring(url.pathname.lastIndexOf('.')).toLowerCase();

    if (VIDEO_EXTENSIONS.has(ext)) {
      params['video-thumbnail'] = 'auto';
      params.fm = 'jpg';
    }

    if (needsImgixServerSign(resolved)) {
      return withByosImgixServerSign(resolved, params);
    }

    url.searchParams.set('dpr', dpr.toString());
    if (options.width) {
      url.searchParams.set('w', options.width.toString());
    }
    if (options.height) {
      url.searchParams.set('h', options.height.toString());
    }
    url.searchParams.set('fit', 'clip');
    url.searchParams.set('auto', 'format');

    if (VIDEO_EXTENSIONS.has(ext)) {
      url.searchParams.set('video-thumbnail', 'auto');
      url.searchParams.set('fm', 'jpg');
    }

    return url.toString();
  } catch {
    return src;
  }
};

/**
 * Extract a human-readable filename from a URL path.
 * Returns null if extraction fails.
 */
export const getFilenameFromUrl = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname;
    const segment = pathname.split('/').pop();

    if (segment) {
      return decodeURIComponent(segment);
    }
  } catch {
    // pass
  }

  return null;
};

/**
 * Resolve a display label for a CanvasTokenNode.
 * Image/video: prefers fileName, falls back to URL filename.
 * Text: truncates textNode.text.
 */
export const getNodeLabel = (node: CanvasTokenNode): string => {
  if (node.type === 'text') {
    const text = node.textNode.text.trim();

    if (text.length > MAX_TEXT_LABEL_LENGTH) {
      return `${text.slice(0, MAX_TEXT_LABEL_LENGTH)}…`;
    }

    return text || 'Text';
  }

  if (node.fileName) {
    return node.fileName;
  }

  return getFilenameFromUrl(node.media.original) ?? (node.type === 'video' ? 'Video' : 'Image');
};

/**
 * Resolve a thumbnail URL for a CanvasTokenNode at the given size.
 * Returns null for text nodes (caller should render an SVG fallback).
 */
export const getNodeThumbnailUrl = (node: CanvasTokenNode, size: number): string | null => {
  if (node.type === 'text') {
    return null;
  }

  const src = node.type === 'video' ? node.media.original : node.media.image || node.media.original;

  return src ? getImageThumbnailUrl(src, { height: size, width: size }) : null;
};

export type GroupMemberCounts = { image: number; video: number; text: number };

/** Build a group label like "Group (2 text, 1 image)". Sorted by count desc, then image > video > text. */
export const getGroupLabel = (counts: GroupMemberCounts): string => {
  const entries = [
    { count: counts.image, label: 'image' },
    { count: counts.video, label: 'video' },
    { count: counts.text, label: 'text' },
  ]
    .filter((e) => e.count > 0)
    .sort((a, b) => b.count - a.count);

  return `Group (${entries.map((e) => `${e.count} ${pluralize(e.label, e.count)}`).join(', ')})`;
};

/** Find a thumbnail URL from a list of CanvasTokenNodes, preferring the first image/video. */
export const getGroupThumbnailUrl = (nodes: CanvasTokenNode[], size: number): string | null => {
  const mediaNode = nodes.find((n) => n.type !== 'text');

  return mediaNode ? getNodeThumbnailUrl(mediaNode, size) : null;
};
